import { ELEMENT_INFO, getSect } from '@jianghu/game-data';
import { STATUS_INFO } from '@jianghu/combat-engine';
import { useEffect, useState } from 'react';
import { useGame } from '../../game/store';

export function Bar({
  value,
  max,
  kind,
  label,
  thin = false,
}: {
  value: number;
  max: number;
  kind: 'hp' | 'mp' | 'sta' | 'xp';
  label?: string;
  thin?: boolean;
}) {
  const frac = max > 0 ? Math.max(0, Math.min(1, value / max)) : 0;
  return (
    <div
      className={`bar ${thin ? 'thin' : ''}`}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={Math.round(max)}
      aria-valuenow={Math.round(value)}
      aria-label={kind}
    >
      <div className={`bar-fill ${kind}`} style={{ transform: `scaleX(${frac})` }} />
      {label !== undefined && <div className="bar-text">{label}</div>}
    </div>
  );
}

export function PlayerFrame() {
  const self = useGame((s) => s.self);
  if (!self) return null;
  const sect = getSect(self.sect);
  const el = sect ? ELEMENT_INFO[sect.element] : null;
  return (
    <section className="hud-frame player-frame" aria-label="Character status">
      <div
        className="portrait"
        style={{ background: `radial-gradient(circle at 40% 35%, ${el?.color ?? '#c9a35a'}, #6a5a3a)` }}
      >
        {sect?.nameZh.slice(0, 1) ?? '侠'}
      </div>
      <div className="level-badge" title="Level">
        {self.level}
      </div>
      <div className="frame-name">
        <span>{self.name}</span>
        <span className="muted" style={{ fontSize: 11 }}>
          {sect?.name}
        </span>
      </div>
      <Bar value={self.hp} max={self.maxHp} kind="hp" label={`${Math.round(self.hp)} / ${self.maxHp}`} />
      <Bar value={self.mp} max={self.maxMp} kind="mp" label={`${Math.floor(self.mp)} / ${self.maxMp}`} />
      <Bar value={self.stamina} max={100} kind="sta" thin />
      {self.statuses.length > 0 && (
        <div className="buffs" aria-label="Status effects">
          {self.statuses.map(([id, ms]) => (
            <div key={id} className="buff" title={STATUS_INFO[id]?.name ?? id}>
              {id === 'slow' ? '慢' : id === 'stun' ? '晕' : '?'}
              <small>{Math.ceil(ms / 1000)}s</small>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export function TargetFrame() {
  const t = useGame((s) => s.target);
  if (!t) return null;
  const statuses = Object.entries(STATUS_INFO).filter(([, info]) => (t.statusBits & info.bit) !== 0);
  return (
    <section className="hud-frame target-frame" aria-label="Target">
      <div className="frame-name">
        <span style={{ color: t.kind === 'monster' ? '#ffb3a6' : t.kind === 'npc' ? '#ecd08e' : '#a8d4ff' }}>
          {t.name}
        </span>
        {t.level !== undefined && <span className="muted">Lv {t.level}</span>}
        {t.dead && <span className="muted">(defeated)</span>}
      </div>
      {t.kind !== 'npc' && (
        <Bar value={t.hp} max={t.maxHp} kind="hp" label={`${Math.max(0, Math.round(t.hp))} / ${t.maxHp}`} />
      )}
      {statuses.length > 0 && (
        <div className="buffs">
          {statuses.map(([id, info]) => (
            <div key={id} className="buff" title={info.name}>
              {id === 'slow' ? '慢' : '晕'}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export function CastBar() {
  const cb = useGame((s) => s.castBar);
  const [, force] = useState(0);
  useEffect(() => {
    if (!cb) return;
    let raf = 0;
    const tick = (): void => {
      force((x) => x + 1);
      if (performance.now() < cb.end) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [cb]);
  if (!cb) return null;
  const frac = Math.min(1, (performance.now() - cb.start) / (cb.end - cb.start));
  if (frac >= 1) return null;
  return (
    <div className="cast-bar pe-none">
      <Bar value={frac} max={1} kind="sta" thin />
      {cb.name}
    </div>
  );
}
